import { api } from './api.js';
import {
  esc, fmtAmt, fmtAmtParts, showToast, fmtDate, STATUS_CFG,
  canViewAccounting, canAccessOwnerPage, canViewOwner,
  colomboMonthRange, colomboYearRange,
} from './utils.js';
import { initPageChrome } from './page-chrome.js';
import { toggleTheme } from './theme-ui.js';
import { initDensityControls } from './density-ui.js';
import { installSessionHandler, clearSessionHint } from './session.js';

installSessionHandler({ redirect: '/' });

const DEPT_LABELS = { tour: 'Tours', visa: 'Visa', ticket: 'Tickets' };
const DEPT_ORDER = ['tour', 'visa', 'ticket'];

function viewerCheck(a) {
  return a && (a.role === 'viewer' || (a.permissions && a.permissions.view_only));
}

function setMoney(el, n) {
  if (!el) return;
  const p = fmtAmtParts(n);
  el.innerHTML = `<span class="currency">${p.currency || ''}</span><span class="amount">${esc(p.amount)}</span>`;
}

let ownerDeptScope = 'my';
let ownerAgent = null;
let ownerData = null;
let finalizedFilter = 'all';
let moneyDept = 'all';
let ownerTab = 'overview';
let canOverview = true;
let canLedger = false;
let summaryFetchGen = 0;

const profitCls = (p) => (p == null ? '' : (p >= 0 ? 'style="color:var(--green);font-weight:700"' : 'style="color:var(--red);font-weight:700"'));
const typeChip = (type) => {
  const t = String(type || '').toLowerCase();
  const cls = ['tour', 'visa', 'ticket'].includes(t) ? t : '';
  return `<span class="type-chip ${cls}">${esc(type)}</span>`;
};
const agentChip = (name, color) =>
  `<span class="agent-chip"><span class="agent-dot" style="background:${esc(color || '#888')}"></span>${esc(name || '—')}</span>`;

function setOwnerTab(tab) {
  if (tab === 'ledger' && canLedger) ownerTab = 'ledger';
  else if (canOverview) ownerTab = 'overview';
  else if (canLedger) ownerTab = 'ledger';
  else ownerTab = 'overview';
  document.querySelectorAll('[data-owner-tab]').forEach((btn) => {
    if (btn.classList.contains('hidden')) return;
    btn.classList.toggle('active', btn.dataset.ownerTab === ownerTab);
  });
  document.getElementById('ownerTabOverview')?.classList.toggle('hidden', ownerTab !== 'overview' || !canOverview);
  document.getElementById('ownerTabLedger')?.classList.toggle('hidden', ownerTab !== 'ledger' || !canLedger);
  if (ownerTab === 'ledger') loadLedger();
}

async function loadLedger() {
  const params = new URLSearchParams();
  const type = document.getElementById('lType')?.value;
  const status = document.getElementById('lStatus')?.value;
  const from = document.getElementById('lFrom')?.value;
  const to = document.getElementById('lTo')?.value;
  if (type) params.set('type', type);
  if (status) params.set('status', status);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  try {
    const data = await api('/api/accounting?' + params);
    const t = data.totals || {};
    document.getElementById('lCount').textContent = t.count || 0;
    setMoney(document.getElementById('lCost'), t.total_cost);
    setMoney(document.getElementById('lSold'), t.total_sold);
    setMoney(document.getElementById('lProfit'), t.total_profit);
    document.getElementById('ledgerBody').innerHTML = (data.items || []).map((i) => `
      <tr>
        <td><strong>${esc(i.name)}</strong></td>
        <td>${typeChip(i.type)}</td>
        <td>${agentChip(i.agent_name, i.agent_color)}</td>
        <td><span class="badge ${(STATUS_CFG[i.status] || STATUS_CFG.new).cls}">${esc((STATUS_CFG[i.status] || STATUS_CFG.new).label)}</span></td>
        <td>${fmtAmt(i.cost_amount)}</td>
        <td>${fmtAmt(i.sold_amount)}</td>
        <td ${profitCls(i.profit)}>${fmtAmt(i.profit)}</td>
        <td style="font-size:11px">${esc(i.last_edited_by_name || '—')}<br><span style="color:var(--fg-dim)">${fmtDate(i.updated_at)}</span></td>
      </tr>`).join('') || '<tr><td colspan="8" class="empty">No records</td></tr>';
    const bt = Object.entries(t.by_type || {}).map(([k, v]) =>
      `<div class="agent-row">${typeChip(k)}<span class="breakdown-stat">${v.count} · profit ${fmtAmt(v.profit)}</span></div>`).join('');
    const ba = Object.entries(t.by_agent || {}).map(([k, v]) =>
      `<div class="agent-row">${agentChip(k, v.color)}<span class="breakdown-stat">${v.count} · profit ${fmtAmt(v.profit)}</span></div>`).join('');
    document.getElementById('ledgerBreakdown').innerHTML =
      `<div><div class="sidebar-label">By type</div>${bt || '<p class="breakdown-empty">—</p>'}</div><div><div class="sidebar-label">By agent</div>${ba || '<p class="breakdown-empty">—</p>'}</div>`;
  } catch (e) {
    document.getElementById('ledgerBody').innerHTML = `<tr><td colspan="8" class="empty">${esc(e.message || 'Load failed')}</td></tr>`;
  }
}

function syncLedgerDatesFromOverview() {
  const { from, to } = getPeriodInputs();
  if (document.getElementById('lFrom')) document.getElementById('lFrom').value = from || '';
  if (document.getElementById('lTo')) document.getElementById('lTo').value = to || '';
}

function getPeriodInputs() {
  return {
    from: document.getElementById('periodFrom')?.value || '',
    to: document.getElementById('periodTo')?.value || '',
  };
}

function ensurePeriodInputs() {
  let { from, to } = getPeriodInputs();
  if (!from || !to || from > to) {
    const cur = colomboMonthRange(0);
    from = cur.from;
    to = cur.to;
    if (document.getElementById('periodFrom')) document.getElementById('periodFrom').value = from;
    if (document.getElementById('periodTo')) document.getElementById('periodTo').value = to;
  }
  return { from, to };
}

function setPeriodRange(range) {
  if (document.getElementById('periodFrom')) document.getElementById('periodFrom').value = range.from;
  if (document.getElementById('periodTo')) document.getElementById('periodTo').value = range.to;
}

async function loadOwnerSummary() {
  const { from, to } = ensurePeriodInputs();
  const params = new URLSearchParams();
  params.set('dept_scope', ownerDeptScope);
  params.set('from', from);
  params.set('to', to);
  return api('/api/owner/summary?' + params);
}

function renderTargetBar(label, progress, formatter) {
  if (!progress || progress.target <= 0) return '';
  const fmt = formatter || ((n) => n);
  const cls = progress.met ? 'target-bar-met' : '';
  return `
    <div class="target-row ${cls}">
      <div class="target-row-head">
        <span>${esc(label)}</span>
        <span>${fmt(progress.actual)} / ${fmt(progress.target)} · ${progress.pct}%</span>
      </div>
      <div class="target-bar"><div class="target-bar-fill" style="width:${progress.pct}%"></div></div>
      ${progress.remaining > 0 ? `<div class="target-remaining">${esc(label)} left: ${fmt(progress.remaining)}</div>` : '<div class="target-remaining target-hit">Target reached</div>'}
    </div>`;
}

function renderDeptCards(data) {
  const el = document.getElementById('deptCards');
  if (!el) return;
  el.innerHTML = DEPT_ORDER.map((t) => {
    const d = data.departments?.[t] || {};
    return `
      <div class="stat-card glass dept-summary-card">
        <div class="stat-label">${DEPT_LABELS[t]}</div>
        <div class="dept-summary-grid">
          <div><span class="k">Leads</span><span class="v">${d.leads || 0}</span></div>
          <div><span class="k">Finalized</span><span class="v">${d.converted || 0}</span></div>
          <div><span class="k">Profit</span><span class="v">${data.show_money ? fmtAmt(d.profit) : '—'}</span></div>
        </div>
      </div>`;
  }).join('');
}

function renderMoney(data) {
  const m = data.money || {};
  const totalEl = document.getElementById('moneyTotal');
  if (totalEl) {
    totalEl.innerHTML = data.show_money ? `
      <div class="money-total-row"><span>Total cost</span><strong>${fmtAmt(m.total_cost)}</strong></div>
      <div class="money-total-row"><span>Total sold</span><strong>${fmtAmt(m.total_sold)}</strong></div>
      <div class="money-total-row accent"><span>Total profit</span><strong>${fmtAmt(m.total_profit)}</strong></div>
    ` : '<p class="owner-section-hint">Profit figures require accounting access.</p>';
  }

  const tabs = document.getElementById('moneyDeptTabs');
  if (tabs) {
    tabs.innerHTML = ['all', ...DEPT_ORDER].map((t) =>
      `<button type="button" class="btn btn-ghost btn-sm${moneyDept === t ? ' active' : ''}" data-money-dept="${t}">${t === 'all' ? 'All' : DEPT_LABELS[t]}</button>`
    ).join('');
    tabs.querySelectorAll('[data-money-dept]').forEach((btn) => {
      btn.onclick = () => {
        moneyDept = btn.dataset.moneyDept;
        renderMoneyDetail(data);
        tabs.querySelectorAll('[data-money-dept]').forEach((b) => b.classList.toggle('active', b.dataset.moneyDept === moneyDept));
      };
    });
  }
  renderMoneyDetail(data);

  const ins = data.insights;
  const insightsEl = document.getElementById('insights');
  if (insightsEl) {
    insightsEl.innerHTML = ins ? `
      <div class="insight-pills">
        <span class="agent-type-pill">Avg profit / booking: ${fmtAmt(ins.avg_profit_per_conversion)}</span>
        <span class="agent-type-pill">Margin: ${ins.margin_pct}%</span>
        <span class="agent-type-pill">Targets met: ${ins.agents_at_target}/${ins.agents_with_target || 0} agents</span>
      </div>` : '';
  }
}

function renderMoneyDetail(data) {
  const m = data.money || {};
  const el = document.getElementById('moneyDeptDetail');
  if (!el) return;
  if (!data.show_money) { el.innerHTML = ''; return; }
  if (moneyDept === 'all') {
    el.innerHTML = DEPT_ORDER.map((t) => {
      const d = m.by_type?.[t] || {};
      return `<div class="money-dept-line"><strong>${DEPT_LABELS[t]}</strong> · ${d.count || 0} finalized · cost ${fmtAmt(d.cost)} · profit ${fmtAmt(d.profit)}</div>`;
    }).join('');
    return;
  }
  const d = m.by_type?.[moneyDept] || {};
  el.innerHTML = `
    <div class="detail-grid" style="grid-template-columns:1fr 1fr">
      <div class="detail-field"><div class="detail-field-label">Finalized</div><div class="detail-field-value">${d.count || 0}</div></div>
      <div class="detail-field"><div class="detail-field-label">Cost</div><div class="detail-field-value">${fmtAmt(d.cost)}</div></div>
      <div class="detail-field"><div class="detail-field-label">Sold</div><div class="detail-field-value">${fmtAmt(d.sold)}</div></div>
      <div class="detail-field"><div class="detail-field-label">Profit</div><div class="detail-field-value">${fmtAmt(d.profit)}</div></div>
    </div>`;
}

function renderAgents(data) {
  const el = document.getElementById('byAgent');
  if (!el) return;
  el.innerHTML = (data.by_agent || []).map((a) => {
    const profitStyle = (a.profit || 0) < 0 ? 'color:var(--red)' : '';
    const typeBlocks = DEPT_ORDER.map((t) => {
      const td = a[t] || {};
      return `<div class="agent-type-block">
        <div class="agent-type-block-title">${DEPT_LABELS[t]}</div>
        <div class="agent-type-block-stats">Leads ${td.leads || 0} · Finalized ${td.converted || 0} · Profit ${data.show_money ? fmtAmt(td.profit) : '—'}</div>
      </div>`;
    }).join('');
    const targets = data.show_money ? [
      renderTargetBar('Profit target', a.progress?.profit, fmtAmt),
      renderTargetBar('Bookings target', a.progress?.converted, (n) => n),
    ].join('') : '';
    return `
      <div class="agent-perf-card">
        <div class="agent-perf-head">
          <div class="agent-perf-name"><span class="agent-dot" style="background:${esc(a.color || '#00AEEF')}"></span>${esc(a.name)}</div>
          <div class="agent-perf-profit" style="${profitStyle}">${data.show_money ? fmtAmt(a.profit) : '—'}</div>
        </div>
        <div class="agent-perf-grid">
          <div class="agent-perf-metric"><div class="k">Leads</div><div class="v">${a.leads}</div></div>
          <div class="agent-perf-metric"><div class="k">Finalized</div><div class="v">${a.converted} <span style="font-size:10px;color:var(--fg-dim)">${a.conversion_rate}%</span></div></div>
          <div class="agent-perf-metric"><div class="k">Cost</div><div class="v">${data.show_money ? fmtAmt(a.cost) : '—'}</div></div>
          <div class="agent-perf-metric"><div class="k">Sold</div><div class="v">${data.show_money ? fmtAmt(a.sold) : '—'}</div></div>
        </div>
        ${typeBlocks}
        ${targets}
      </div>`;
  }).join('') || '<p style="color:var(--fg-dim)">No agents in this period</p>';
}

function renderFinalizedTable(data) {
  const tabs = document.getElementById('finalizedTabs');
  if (!tabs) return;
  tabs.innerHTML = ['all', ...DEPT_ORDER].map((t) =>
    `<button type="button" class="btn btn-ghost btn-sm${finalizedFilter === t ? ' active' : ''}" data-finalized="${t}">${t === 'all' ? 'All' : DEPT_LABELS[t]}</button>`
  ).join('');
  tabs.querySelectorAll('[data-finalized]').forEach((btn) => {
    btn.onclick = () => {
      finalizedFilter = btn.dataset.finalized;
      renderFinalizedRows(data);
      tabs.querySelectorAll('[data-finalized]').forEach((b) => b.classList.toggle('active', b.dataset.finalized === finalizedFilter));
    };
  });
  renderFinalizedRows(data);
}

function renderFinalizedRows(data) {
  const body = document.getElementById('finalizedBody');
  if (!body) return;
  const rows = (data.finalized || []).filter((r) => finalizedFilter === 'all' || r.type === finalizedFilter);
  body.innerHTML = rows.length ? rows.map((r) => `
    <tr>
      <td>${esc(fmtDate(r.inquiry_date || r.updated_at))}</td>
      <td><strong>${esc(r.name)}</strong></td>
      <td>${esc(DEPT_LABELS[r.type] || r.type)}</td>
      <td>${esc(r.agent_name || '—')}</td>
      <td>${data.show_money ? fmtAmt(r.cost_amount) : '—'}</td>
      <td>${data.show_money ? fmtAmt(r.profit) : '—'}</td>
    </tr>`).join('') : '<tr><td colspan="6" style="color:var(--fg-dim)">No finalized entries in this period</td></tr>';
}

function renderRecent(data) {
  const body = document.getElementById('recentBody');
  if (!body) return;
  body.innerHTML = (data.recent || []).length ? data.recent.map((r) => `
    <tr>
      <td>${esc(fmtDate(r.inquiry_date || r.updated_at))}</td>
      <td><strong>${esc(r.name)}</strong></td>
      <td>${esc(DEPT_LABELS[r.type] || r.type)}</td>
      <td>${esc(r.agent_name || '—')}</td>
      <td>${data.show_money ? fmtAmt(r.profit) : '—'}</td>
    </tr>`).join('') : '<tr><td colspan="5" style="color:var(--fg-dim)">No recent finalized activity</td></tr>';
}

async function renderOwnerSummary() {
  if (!canOverview) return;
  const gen = ++summaryFetchGen;
  const label = document.getElementById('periodLabel');
  if (label) label.textContent = 'Loading overview…';
  try {
    const s = await loadOwnerSummary();
    if (gen !== summaryFetchGen) return;
    if (!s?.period) throw new Error('Overview response was incomplete');
    ownerData = s;
    if (label) {
      label.textContent = `Showing ${fmtDate(s.period.from)} — ${fmtDate(s.period.to)} (finalized profit uses accurate tour markup)`;
    }
    document.getElementById('sLeads').textContent = s.summary?.leads ?? 0;
    document.getElementById('sConv').textContent = s.summary?.converted ?? 0;
    document.getElementById('sRate').textContent = `${s.summary?.conversion_rate || 0}% conversion`;
    setMoney(document.getElementById('sProfit'), s.money?.total_profit);
    document.getElementById('sMargin').textContent = s.insights ? `${s.insights.margin_pct}%` : '—';
    document.getElementById('sAvgProfit').textContent = s.insights ? `Avg ${fmtAmt(s.insights.avg_profit_per_conversion)} / booking` : '—';
    renderDeptCards(s);
    renderAgents(s);
    renderMoney(s);
    renderFinalizedTable(s);
    renderRecent(s);
  } catch (e) {
    if (gen !== summaryFetchGen) return;
    if (label) label.textContent = 'Could not load overview — try Apply or This month';
    showToast(e.message || 'Could not load overview', 'error');
  }
}

function showOwnerDeptToggle(agent) {
  const wrap = document.getElementById('ownerDeptScope');
  const show = agent && agent.role !== 'admin' && agent.departments?.length && agent.departments.length < 3;
  wrap?.classList.toggle('hidden', !show);
}

function setOwnerDeptScope(scope) {
  ownerDeptScope = scope === 'all' ? 'all' : 'my';
  document.getElementById('ownerDeptMy')?.classList.toggle('active', ownerDeptScope !== 'all');
  document.getElementById('ownerDeptAll')?.classList.toggle('active', ownerDeptScope === 'all');
  renderOwnerSummary();
}

function applyPreset(preset) {
  let range;
  if (preset === 'last-month') range = colomboMonthRange(-1);
  else if (preset === 'year') range = colomboYearRange();
  else range = colomboMonthRange(0);
  setPeriodRange(range);
  if (canOverview) renderOwnerSummary();
}

async function boot() {
  try {
    const { agent } = await api('/api/me');
    if (agent.must_change_password) {
      location.href = '/';
      return;
    }
    ownerAgent = agent;
    canOverview = canViewOwner(agent);
    canLedger = canViewAccounting(agent);
    if (!canAccessOwnerPage(agent)) {
      location.href = '/';
      return;
    }
    document.getElementById('agentName').textContent = agent.name;
    document.getElementById('agentDot').style.background = agent.color || '#00AEEF';
    if (!viewerCheck(agent)) document.getElementById('trackerLink').classList.remove('hidden');
    else document.getElementById('trackerLink').style.display = 'none';
    initPageChrome(agent);
    initDensityControls();
    document.getElementById('themeToggle')?.addEventListener('click', toggleTheme);
    document.getElementById('ownerOverviewControls')?.classList.toggle('hidden', !canOverview);
    document.getElementById('ownerTabOverview')?.classList.toggle('hidden', !canOverview);
    document.getElementById('ledgerTabBtn')?.classList.toggle('hidden', !canLedger);
    document.querySelector('[data-owner-tab="overview"]')?.classList.toggle('hidden', !canOverview);
    if (!canOverview && canLedger) {
      document.querySelector('[data-owner-tab="overview"]')?.classList.remove('active');
      document.getElementById('ledgerTabBtn')?.classList.remove('hidden');
    }
    document.querySelectorAll('[data-owner-tab]').forEach((btn) => {
      btn.addEventListener('click', () => setOwnerTab(btn.dataset.ownerTab));
    });
    document.getElementById('ledgerReload')?.addEventListener('click', loadLedger);
    showOwnerDeptToggle(agent);
    document.getElementById('ownerDeptMy')?.addEventListener('click', () => setOwnerDeptScope('my'));
    document.getElementById('ownerDeptAll')?.addEventListener('click', () => setOwnerDeptScope('all'));
    setPeriodRange(colomboMonthRange(0));
    syncLedgerDatesFromOverview();
    document.getElementById('applyPeriod')?.addEventListener('click', () => {
      syncLedgerDatesFromOverview();
      if (canOverview) renderOwnerSummary();
      else if (ownerTab === 'ledger') loadLedger();
    });
    document.querySelectorAll('[data-preset]').forEach((btn) => {
      btn.addEventListener('click', () => {
        applyPreset(btn.dataset.preset);
        syncLedgerDatesFromOverview();
        if (!canOverview && ownerTab === 'ledger') loadLedger();
      });
    });
    const startTab = new URLSearchParams(location.search).get('tab');
    if (canOverview) await renderOwnerSummary();
    else setOwnerTab('ledger');
    if (startTab === 'ledger' && canLedger) setOwnerTab('ledger');
    else if (!canOverview && canLedger) setOwnerTab('ledger');
  } catch (e) {
    if (e?.status === 401) location.href = '/';
    else showToast(e?.message || 'Could not open overview', 'error');
  }
}

document.getElementById('logoutBtn')?.addEventListener('click', async () => {
  try { await api('/api/logout', { method: 'POST' }); } catch { /* ignore */ }
  clearSessionHint();
  location.href = '/';
});

boot();
